// src/hooks/useBookmarkFavorite.js
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthStore from '../store/authStore';
import { USER_ROLES } from '../config/auth0.config';
import {
  isBookmarked as checkBookmark,
  createBookmark as addBookmark,
  deleteBookmark as removeBookmark,
} from '../api/user-service/bookmarkService';
import {
  isFavorite as checkFavorite,
  likeFavorite as addFavorite,
  unlikeFavorite as removeFavorite,
} from '../api/user-service/favoriteService';

/**
 * Custom hook for managing bookmarks and favorites
 * @param {string} targetType - 'FARM' | 'EXPERIENCE' | 'STAY'
 * @param {number} targetId - Target item ID
 * @returns {Object} Bookmark and favorite state and actions
 */
export function useBookmarkFavorite(targetType, targetId) {
  const { isAuthenticated } = useAuth0();
  const { role } = useAuthStore();
  
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  
  const [isLoadingBookmark, setIsLoadingBookmark] = useState(false);
  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
  
  const [bookmarkError, setBookmarkError] = useState(null);
  const [favoriteError, setFavoriteError] = useState(null);

  // All authenticated users (USER, FARMER, ADMIN) can bookmark/favorite
  const canUseFeatures = isAuthenticated && !!role;

  // Check bookmark status
  useEffect(() => {
    const checkBookmarkStatus = async () => {
      if (!canUseFeatures || !targetId) return;
      
      setIsLoadingBookmark(true);
      setBookmarkError(null);
      
      try {
        const response = await checkBookmark(targetType, targetId);
        // response: { bookmarked: boolean, bookmarkId?: number } (already unwrapped)
        const data = response?.data ?? response;
        setIsBookmarked(data?.bookmarked || false);
        setBookmarkId(data?.bookmarkId || null);
      } catch (error) {
        console.error('Failed to check bookmark:', error);
        setBookmarkError(error.message);
      } finally {
        setIsLoadingBookmark(false);
      }
    };

    checkBookmarkStatus();
  }, [canUseFeatures, targetType, targetId]);

  // Check favorite status
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!canUseFeatures || !targetId) return;
      
      setIsLoadingFavorite(true);
      setFavoriteError(null);
      
      try {
        const response = await checkFavorite(targetType, targetId);
        // response: { favorited: boolean, favoriteId?: number } (already unwrapped)
        const data = response?.data ?? response;
        setIsFavorited(data?.favorited || false);
        setFavoriteId(data?.favoriteId || null);
      } catch (error) {
        console.error('Failed to check favorite:', error);
        setFavoriteError(error.message);
      } finally {
        setIsLoadingFavorite(false);
      }
    };

    checkFavoriteStatus();
  }, [canUseFeatures, targetType, targetId]);

  // Toggle bookmark
  const toggleBookmark = async () => {
    if (!canUseFeatures || !targetId) return;
    
    setIsLoadingBookmark(true);
    setBookmarkError(null);
    
    try {
      if (isBookmarked) {
        // Remove bookmark
        await removeBookmark(bookmarkId);
        setIsBookmarked(false);
        setBookmarkId(null);
      } else {
        // Add bookmark
        const response = await addBookmark({ targetType, targetId });
        setIsBookmarked(true);
        const data = response?.data ?? response;
        setBookmarkId(data?.id || null);
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      setBookmarkError(error.message);
    } finally {
      setIsLoadingBookmark(false);
    }
  };

  // Toggle favorite
  const toggleFavorite = async () => {
    if (!canUseFeatures || !targetId) return;
    
    setIsLoadingFavorite(true);
    setFavoriteError(null);
    
    try {
      if (isFavorited) {
        // Remove favorite
        await removeFavorite(targetType, targetId);
        setIsFavorited(false);
        setFavoriteId(null);
      } else {
        // Add favorite
        const response = await addFavorite({ targetType, targetId });
        setIsFavorited(true);
        const data = response?.data ?? response;
        setFavoriteId(data?.id || null);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      setFavoriteError(error.message);
    } finally {
      setIsLoadingFavorite(false);
    }
  };

  return {
    // Bookmark state
    isBookmarked,
    bookmarkId,
    isLoadingBookmark,
    bookmarkError,
    toggleBookmark,
    
    // Favorite state
    isFavorited,
    favoriteId,
    isLoadingFavorite,
    favoriteError,
    toggleFavorite,
    
    // General state
    canUseFeatures,
  };
}
